import React from 'react';
import { 
  HeartHandshake, 
  Briefcase, 
  Stethoscope, 
  Scale, 
  Building2, 
  Cpu, 
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

interface IndustriesWeServeProps {
  onOpenBooking: () => void;
}

const customerCareOfficeBg = '/src/assets/images/xweba_office_customer_care_bg_1793403437018.jpg';

export const IndustriesWeServe: React.FC<IndustriesWeServeProps> = ({ onOpenBooking }) => {
  const [bgFailed, setBgFailed] = React.useState(false);

  const industries = [
    {
      name: 'Non-Profits & NGOs',
      badge: 'Featured',
      icon: HeartHandshake,
      description: 'Make your mission discoverable in AI research engines, attract institutional donors, and automate volunteer & partner onboarding with intelligent conversational workflows.',
      outcomes: [
        'Donor & grant discoverability in Perplexity/ChatGPT',
        'Structured NGO entity graphs & impact reporting',
        '24/7 automated donor inquiries & volunteer routing',
      ],
    },
    {
      name: 'B2B & Professional Services',
      badge: 'High Impact',
      icon: Briefcase,
      description: 'Position your firm as the verified industry authority. Eliminate slow email follow-ups with instant WhatsApp alerts and automated CRM pipeline synchronization.',
      outcomes: [
        'Top-tier entity citation over generic directories',
        'Pre-qualified corporate prospect scoring',
        'Direct calendar consultation booking',
      ],
    },
    {
      name: 'Healthcare & Aesthetics',
      badge: '24/7 Inbound',
      icon: Stethoscope,
      description: 'Capture after-hours and weekend patient inquiries that traditional contact forms lose. Pre-screen procedure intent and automate appointment bookings.',
      outcomes: [
        '94% after-hours and weekend lead retention',
        'Autonomous clinical triage & FAQ answering',
        'Instant WhatsApp confirmation & reminders',
      ],
    },
    {
      name: 'Legal & Corporate Advisory',
      badge: 'Authority',
      icon: Scale,
      description: 'Dominate conversational legal queries in your jurisdiction. Educate prospective clients with question-answering architecture and pre-screen retainer fit.',
      outcomes: [
        'Verifiable Schema.org legal entity graphs',
        'Automated conflict & case scope pre-qualification',
        'Encrypted lead routing to practice heads',
      ],
    },
    {
      name: 'Real Estate & Developments',
      badge: 'Fast Turnaround',
      icon: Building2,
      description: 'Showcase luxury listings and commercial properties with lightning-fast mobile performance. Qualify investor budgets before scheduling viewings.',
      outcomes: [
        'High-resolution imagery at sub-second load times',
        'Automated buyer qualification & brochure delivery',
        'Instant WhatsApp dispatch to property agents',
      ],
    },
    {
      name: 'SaaS & Technology Startups',
      badge: 'Growth Engine',
      icon: Cpu,
      description: 'Surpass competitor SEO by capturing high-intent conversational searches in ChatGPT and Claude. Direct qualified enterprise demos directly into your CRM.',
      outcomes: [
        'Generative Engine Optimization (GEO) dominance',
        'Automated pipeline sync (HubSpot, Salesforce)',
        'Custom interactive ROI & scope calculators',
      ],
    },
  ];

  return (
    <section id="industries" className="relative py-16 sm:py-20 bg-slate-50/50 md:bg-white border-b border-slate-200 overflow-hidden">
      {/* Faint Background Image of Xweba Branded Office Customer Care (Proportioned for Mobile & Desktop) */}
      <div className="absolute inset-x-0 top-0 h-[420px] sm:h-[520px] md:inset-0 md:h-full pointer-events-none select-none overflow-hidden" aria-hidden="true">
        {!bgFailed ? (
          <img
            src={customerCareOfficeBg}
            alt="Xweba branded customer care office team"
            referrerPolicy="no-referrer"
            onError={() => setBgFailed(true)}
            className="w-full h-full object-cover object-[72%_center] md:object-center opacity-[0.22] md:opacity-[0.16]"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-slate-50 via-white to-slate-50" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/45 to-slate-50/95 md:from-white/75 md:via-white/45 md:to-white/80" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-slate-600">
            <span className="text-[#F26522] font-black">TAILORED EXPERTISE</span>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <span>INDUSTRIES WE SERVE</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B2D52] tracking-tight">
            Tailored AI architectures for high-impact sectors
          </h2>

          <p className="text-slate-700 text-base sm:text-lg">
            Whether you are a global non-profit scaling impact or a growing enterprise closing high-ticket deals, our AI-ready web platforms are tailored to your sector's distinct conversion journeys.
          </p>
        </div>

        {/* 6 Industries Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {industries.map((ind, idx) => {
            const Icon = ind.icon;
            const isNonProfit = ind.name.includes('Non-Profit');

            return (
              <div
                key={idx}
                className={`p-6 sm:p-7 rounded-3xl border transition-all duration-200 flex flex-col justify-between backdrop-blur-xs ${
                  isNonProfit
                    ? 'bg-gradient-to-br from-orange-50/85 via-white/90 to-slate-50/85 border-orange-300 shadow-sm ring-1 ring-orange-200'
                    : 'bg-white/85 hover:bg-white/95 border-slate-200/90 hover:border-slate-300 shadow-2xs'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-2xl ${
                      isNonProfit ? 'bg-[#F26522] text-white' : 'bg-white border border-slate-200 text-[#0B2D52]'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                      isNonProfit
                        ? 'bg-orange-100 text-[#F26522] font-black'
                        : 'bg-slate-200/80 text-slate-700'
                    }`}>
                      {ind.badge}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-lg font-extrabold text-[#0B2D52]">
                      {ind.name}
                    </h3>
                    <p className="text-xs text-slate-600 leading-relaxed mt-1.5">
                      {ind.description}
                    </p>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-slate-200/60">
                    {ind.outcomes.map((item, iIdx) => (
                      <div key={iIdx} className="flex items-start gap-2 text-xs text-slate-700">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#F26522] shrink-0 mt-0.5" />
                        <span className="leading-tight">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 mt-4">
                  <button
                    type="button"
                    onClick={onOpenBooking}
                    className="py-2.5 px-3 -ml-3 min-h-[44px] rounded-xl text-xs font-bold text-[#0B2D52] hover:text-[#F26522] hover:bg-slate-100/80 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <span>Deploy for {ind.name}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};
